// @hssna/family-web 设备降级 + 边缘节点断网/恢复 全局横幅 (T4-08)
// 规格: SPEC §8.1 / 战役 4 T4-08 / HA 自治设计
//
// 横幅规则:
//   - 设备降级 → 橙色横幅 "设备 {name} {issue},请检查"
//   - 边缘节点离线 → 红色横幅 "边缘节点离线,本地告警仍可工作 (24h 自治)"
//   - 同步失败 → 橙色横幅 "数据同步异常,本地告警不受影响"
//   - 已恢复 → 绿色横幅 "已恢复" (5s 后自动消失)
//
// 优先级: 离线 > 降级 > 同步失败 > 恢复
// 数据源: useDeviceStatus + useEdgeSyncStatus

import { useEffect, useRef, useState } from 'react';
import { Badge } from '@hssna/ui-kit';
import { useDeviceStatus } from '../hooks/useDeviceStatus';
import type { DeviceWithStatus } from '../hooks/useDeviceStatus';
import { useEdgeSyncStatus } from '../hooks/useEdgeSyncStatus';
import type { EdgeSyncStatus } from '../hooks/useEdgeSyncStatus';

/** 横幅视觉类型 */
type BannerKind = 'offline' | 'degraded' | 'sync-failed' | 'recovered' | 'none';

/** 横幅文案配置 */
interface BannerContent {
  kind: BannerKind;
  /** 主要消息 (粗体) */
  title: string;
  /** 次要消息 (灰色补充) */
  detail?: string;
  /** 徽章标签 */
  badge: string;
  /** 徽章变体 */
  badgeVariant: 'danger' | 'warning' | 'success';
}

/** 设备降级类型 → 中文描述 */
const DEGRADATION_LABELS: Record<string, string> = {
  battery_low: '电量不足',
  sensor_fault: '传感器故障',
  connectivity_issue: '连接异常',
  offline: '离线',
  firmware_bug: '固件异常',
  coverage_gap: '覆盖缺口',
};

/** 取设备展示名 (优先 model, 其次 device_sn, 最后 id) */
function getDeviceName(d: DeviceWithStatus): string {
  return d.model || d.device_sn || d.id;
}

/** 取设备降级文案 */
function getDegradationIssue(d: DeviceWithStatus): string {
  if (d.degradation?.type) {
    return DEGRADATION_LABELS[d.degradation.type] ?? d.degradation.type;
  }
  if (d.degradation?.message) {
    return d.degradation.message;
  }
  if (d.status === 'offline') return '离线';
  if (d.status === 'error') return '异常';
  return '异常';
}

/** 设备横幅内容 */
function buildDeviceBanner(device: DeviceWithStatus): BannerContent {
  const name = getDeviceName(device);
  const issue = getDegradationIssue(device);
  return {
    kind: 'degraded',
    title: `设备 ${name} ${issue},请检查`,
    detail: device.degradation?.message ?? undefined,
    badge: '设备降级',
    badgeVariant: 'warning',
  };
}

/** 边缘横幅内容 */
function buildEdgeBanner(status: EdgeSyncStatus): BannerContent | null {
  switch (status) {
    case 'offline':
      return {
        kind: 'offline',
        title: '边缘节点离线,本地告警仍可工作 (24h 自治)',
        detail: '云端同步已暂停,边缘 AI 与边缘网关进入自治模式',
        badge: '边缘离线',
        badgeVariant: 'danger',
      };
    case 'degraded':
      return {
        kind: 'sync-failed',
        title: '数据同步异常,本地告警不受影响',
        detail: '边缘节点与云端之间同步出现延迟,正在自动重试',
        badge: '同步异常',
        badgeVariant: 'warning',
      };
    case 'online':
      // 在线状态由恢复横幅逻辑处理 (lastEvent 触发)
      return null;
    default:
      return null;
  }
}

const BANNER_STYLE: Record<BannerKind, string> = {
  offline:
    'border-alert-red bg-alert-red/10 text-alert-red',
  degraded:
    'border-alert-yellow bg-alert-yellow/10 text-alert-orange',
  'sync-failed':
    'border-alert-yellow bg-alert-yellow/10 text-alert-orange',
  recovered:
    'border-health-l1 bg-health-l1/10 text-health-l1',
  none: '',
};

const RECOVERED_AUTO_DISMISS_MS = 5_000;

/**
 * 设备状态 + 边缘节点状态 全局横幅
 *
 * 挂载位置: AppLayout 顶部导航栏下方
 * 行为:
 *   - 自动聚合 useDeviceStatus 与 useEdgeSyncStatus
 *   - 优先显示最高严重级别的横幅
 *   - "已恢复" 横幅 5s 后自动消失
 */
export function DeviceStatusBanner() {
  const { degradedDevices } = useDeviceStatus();
  const { status: edgeStatus, lastEvent } = useEdgeSyncStatus();

  // 上一次边缘状态 (用于检测 "从异常恢复" 的时机)
  const prevEdgeStatusRef = useRef<EdgeSyncStatus | null>(null);
  // 是否显示"已恢复"横幅 (5s 后自动消失)
  const [showRecovered, setShowRecovered] = useState(false);

  // 检测边缘状态从 offline/degraded → online 的恢复时机
  useEffect(() => {
    const prev = prevEdgeStatusRef.current;
    prevEdgeStatusRef.current = edgeStatus;

    if (
      prev !== null &&
      prev !== 'online' &&
      edgeStatus === 'online' &&
      lastEvent &&
      (lastEvent.type === 'edge.online' || lastEvent.type === 'sync.recovered')
    ) {
      setShowRecovered(true);
    }
  }, [edgeStatus, lastEvent]);

  // "已恢复" 横幅 5s 后自动消失
  useEffect(() => {
    if (!showRecovered) return;
    const timer = setTimeout(() => {
      setShowRecovered(false);
    }, RECOVERED_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [showRecovered]);

  // 计算当前应展示的横幅 (优先级: offline > degraded > sync-failed > recovered)
  let banner: BannerContent | null = null;

  if (edgeStatus === 'offline') {
    banner = buildEdgeBanner('offline');
  } else if (degradedDevices.length > 0) {
    // 设备降级横幅 (取第一个降级设备)
    banner = buildDeviceBanner(degradedDevices[0]);
    if (degradedDevices.length > 1) {
      banner = {
        ...banner,
        detail: `共 ${degradedDevices.length} 台设备异常,详见设备页`,
      };
    }
  } else if (edgeStatus === 'degraded') {
    banner = buildEdgeBanner('degraded');
  } else if (showRecovered) {
    banner = {
      kind: 'recovered',
      title: '已恢复',
      detail: '边缘节点与设备状态已恢复正常',
      badge: '已恢复',
      badgeVariant: 'success',
    };
  }

  if (!banner || banner.kind === 'none') {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex items-center gap-3 border-b px-4 py-2 text-sm ${BANNER_STYLE[banner.kind]}`}
    >
      <Badge variant={banner.badgeVariant}>{banner.badge}</Badge>
      <span className="font-semibold">{banner.title}</span>
      {banner.detail ? (
        <span className="text-xs opacity-80">· {banner.detail}</span>
      ) : null}
      {banner.kind === 'degraded' && degradedDevices.length > 0 ? (
        <span className="ml-auto text-xs opacity-70">
          {degradedDevices.map((d) => getDeviceName(d)).join(' / ')}
        </span>
      ) : null}
    </div>
  );
}
